import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { mySurveysApi } from '../mySurveysApi';
import { TRAINING_RESULT, type MySurveyTraining, type TrainingResultValue } from '../mySurveyTypes';

const ID = 'my-survey-complete-training';

/** The radio's values are strings; the wire's are numbers — `String()` in, `Number()` back out. */
type ResultChoice = '' | '0' | '1';

/**
 * End training — offered as soon as the training is STARTED (product call, 13 Oct 2026; it used to
 * wait for the end date). The employee says how it ended — the RESULT, Completed or Cancelled — and
 * the REASON, required for a cancellation only; Learning & Development reads them on the recommendation.
 *
 * A FORM rather than a confirm dialog, because it COLLECTS. Ending is FINAL — the training is no
 * longer corrected afterwards — so the consequence is stated before the tick. A CANCELLED training
 * takes no report, so the optional "proceed to the report" tick is offered for a completion only.
 * NO Autogenerate: the answer is the employee's own account of a training they took.
 */
export function MySurveyCompleteTrainingModal({
  surveyId,
  recommendationId,
  training,
  onClose,
  onCompleted,
}: {
  surveyId: string;
  /** The recommendation whose training is being ended — the employee's own track on it. */
  recommendationId: string;
  training: MySurveyTraining;
  onClose: () => void;
  /**
   * After the training is ended. `proceedToReport` is the employee's own choice on the form —
   * OPTIONAL, unticked by default, and only ever true for a COMPLETED training: the caller then
   * opens the report form straight away. The report can always be submitted later.
   */
  onCompleted: (proceedToReport: boolean) => void;
}) {
  const toast = useToast();

  // Nothing preselected: the result is the employee's answer, not a default they tick past.
  const [result, setResult] = useState<ResultChoice>('');
  const [reason, setReason] = useState('');
  // Optional — where to go next, not part of what is saved.
  const [proceedToReport, setProceedToReport] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const completing = result === String(TRAINING_RESULT.Completed);
  // The reason is owed for a CANCELLATION only — a completion may leave it empty.
  const reasonMissing = result === String(TRAINING_RESULT.Cancelled) && reason.trim() === '';
  const valid = result !== '' && !reasonMissing;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await mySurveysApi.completeRecommendationTraining(surveyId, recommendationId, {
        result: Number(result) as TrainingResultValue,
        reason: reason.trim() || null,
      });
      toast.success(completing ? 'Completed your training.' : 'Cancelled your training.');
      onCompleted(completing && proceedToReport);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Your training could not be ended.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="End training"
      size="lg"
      id={`${ID}-modal`}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button id={`${ID}-submit`} variant="primary" onClick={() => void save()} disabled={!canSave} loading={saving} loadingLabel="Submitting">
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* What is being ended — read-only facts, so the table display. */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Title', value: training.title },
            { label: 'Start date', value: formatDateOnly(training.startDate), mono: true },
            { label: 'End date', value: formatDateOnly(training.endDate), mono: true },
          ]}
        />

        {/* THE CONSEQUENCE BEFORE THE TICK. */}
        <p className="text-xs text-slate-600">
          This ends your training. It is final: the training can no longer be edited afterwards, and a cancelled training takes no report.
        </p>

        <RadioGroup<ResultChoice>
          id={`${ID}-result`}
          label="Result"
          required
          value={result}
          onChange={setResult}
          options={[
            { value: String(TRAINING_RESULT.Completed) as ResultChoice, label: 'Completed', description: 'You finished the training.' },
            { value: String(TRAINING_RESULT.Cancelled) as ResultChoice, label: 'Cancelled', description: 'The training stopped before it was finished.' },
          ]}
          error={result !== '' ? error?.fieldError('result') : 'Choose the result.'}
          disabled={saving}
        />

        <Textarea
          id={`${ID}-reason`}
          label="Reason"
          required={!completing}
          maxLength={2000}
          description="Why the training ended this way. Learning & Development reads this on the recommendation."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reasonMissing ? 'Give the reason the training was cancelled.' : error?.fieldError('reason')}
          disabled={saving}
        />

        {/* OPTIONAL, and for a COMPLETION only: a cancelled training takes no report. */}
        {completing ? (
          <label htmlFor={`${ID}-proceed-to-report`} className="flex items-start gap-2.5 text-sm text-slate-700">
            <Checkbox
              id={`${ID}-proceed-to-report`}
              className="mt-0.5"
              checked={proceedToReport}
              onChange={(e) => setProceedToReport(e.target.checked)}
              disabled={saving}
            />
            <span>
              Proceed to submitting the report
              <span className="block text-xs text-slate-500">Optional. You can also submit it later, under Report.</span>
            </span>
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
